import React, { useState, useContext } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import decoder from 'jwt-decode'
import { loadProfile, getStreamChatToken } from '../../api'
import { withRouter } from 'react-router-dom'
import { useSpring, animated } from 'react-spring'
import AvatarLogo from './../../images/theme/avatar/login-logo.png';
import FBBCLogo from './../../images/theme/fbbc/fbbc-logo.png'
import Input from '../../components/input.js'
import Button from '../../components/button.js'
import { portalLogin, portalLoginFBBC } from '../../api'
import { ThemeContext } from 'styled-components'
import { getErrorMessage } from './error-codes'
import { ErrorMessage } from '../../components/error-message'

const Page = styled.div`
  background: #FFF;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
`;

const Container = styled(animated.div)`
  border-radius: 8px;
  box-shadow: 0px 0px 15px 0px #cccfd1;
  display: flex;

  @media(max-width: 1020px) {
    margin: 0px 16px;
    width: 100%;
  }
`;

const Banner = styled.div`
  background: ${props => props.theme.color.logoBackground};
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  width: 500px;

  img {
    display: block;
    height: auto;
    width: 280px;
  }

  @media(max-width: 1020px) {
    display: none;
  }
`;

const Form = styled.div`
  background: #FFF;
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  padding: 64px 48px;
  width: 500px;

  @media(max-width: 1020px) {
    padding: 32px 24px;
    width: 100%;
  }
`;

const Title = styled.h2`
  color: #555;
  font-family: ${props => props.theme.font.primary};
  font-size: 1.25rem;
  font-weight: 500;
  margin: 0 0 32px 0;
  text-align: center;
`;

const ForgotLink = styled(Link)`
  color: #888;
  font-family: ${props => props.theme.font.primary};
  font-size: .9375rem;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const LoginButton = styled(Button)`
  font-size: 1.125rem;
  font-weight: 500;
  padding: 15px 0px;
  height: unset;
  margin: 24px auto 0px auto;
  width: 240px;
`;

const mapLogos = {
  "AVATAR": AvatarLogo,
  "FBBC": FBBCLogo,
}

const Login = (props) => {
  const theme = useContext(ThemeContext)
  const formAnimation = useSpring({ opacity: 1, from: { opacity: 0 } })
  const [error, updateError] = useState(false)
  const [errorMessage, setErrorMessage] = useState(false)
  const [loading, updateLoading] = useState(false)
  const [email, updateEmail] = useState('')
  const [password, updatePassword] = useState('')

  const getProfile = () => {
    const jwt = window.localStorage.getItem('jwt')

    if(jwt) {
      const userId = decoder(jwt).id
      getStreamChatToken(userId).then((res) => {
        window.localStorage.setItem('streamChatUser', res.data.id)
        window.localStorage.setItem('streamChatToken', res.data.token)
      }).catch((err) => {
        console.log(err)
      });
      loadProfile(userId).then((res) => {
        if (res.data.profile.active === 1) {
          props.updateUser(res.data.profile)
          props.updateAuth(true)
          if (res.data.profile.password_set === 1) {
            if (res.data.profile.agree_to_terms === null) {
              props.history.push('/terms')
            } else {
              props.history.push('/dashboard/overview')
            }
          } else {
            props.history.push('/account-setup')
          }
        } else {
          // Inactive account
          props.updateAuth(false)
          updateLoading(false)
        }
      }).catch((err) => {
        // Server Error
        props.updateAuth(false)
        updateLoading(false)
      })
    } else {
      // No token
      props.updateAuth(false)
      updateLoading(false)

    }
  }

  const handleEnter = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleLogin(e)
    }
  }

  const handleLoginError = (errCode) => {
    setErrorMessage(getErrorMessage(errCode))
    updateLoading(false)
    updateError(true)
  }

  const handleLogin = (e) => {
    e.preventDefault()

    updateLoading(true)
    if (theme.name === 'FBBC') {
      portalLoginFBBC(email, password).then(res => {  
        setTimeout(() => {
          window.localStorage.setItem('jwt', res.data.token)
          props.updateAuth(true)
          getProfile()
        }, 1500);
      }).catch((err) => { 
        setTimeout(() => {
          updateLoading(false)
          if(err?.response?.data?.error_code) {
            handleLoginError(err.response.data.error_code)
          }
        }, 1500);   
      })
    } else {
      portalLogin(email, password).then(res => {  
        setTimeout(() => {
          window.localStorage.setItem('jwt', res.data.token)
          props.updateAuth(true)
          getProfile()
        }, 1500);
      }).catch((err) => { 
        setTimeout(() => {
          updateLoading(false)
          if(err?.response?.data?.error_code) {
            handleLoginError(err.response.data.error_code)
          }
        }, 1500);   
      })
    }
  }
  
  return (
    <>
      {process.env.REACT_APP_STAGE !== 'prod' &&
        <div style={{background: '#ffc700', textAlign: 'center', width: '100%', position: 'fixed', bottom: 0, left: 0, zIndex: 200, pointerEvents: 'none'}}>
          <p style={{color: '#000', fontSize: '.9rem'}}>This is the <b>{process.env.REACT_APP_STAGE}</b> environment.</p>
        </div>
      }
      <Page>
        <Container style={formAnimation}>
          <Banner>
            <img src={mapLogos[theme.name]} alt="logo" />
          </Banner>
          <Form>
            <Title>Portal Login</Title>
            <Input
              error={Boolean(error)}
              label="Email"
              type="email" 
              autoComplete="email" 
              value={email}
              onChange={updateEmail}
            />
            <Input
              error={Boolean(error)}
              label="Password"
              type="password" 
              autoComplete="current-password" 
              value={password}
              onChange={updatePassword}
              onKeyPress={handleEnter}
            />
            {error && 
              <ErrorMessage>
                {errorMessage}
              </ErrorMessage>
            }
            <ForgotLink to="/reset">Forgot your password?</ForgotLink>
            <LoginButton 
              label="Submit"
              isLoading={loading}
              disabled={loading}
              onClick={handleLogin}
            />
          </Form>
        </Container>
      </Page>
    </>
  )
}


export default withRouter(Login)
